//replaces app/components/widget/widget-add-to-dashboard-modal.html
//replaces app/components/widget/AddToDashboardController.ts

import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import cn from "classnames";
import get from "lodash/get";

import { SWReactIcons } from "@similarweb/icons";
import { CircularLoader, PlainTooltip, Textfield } from "@similarweb/ui-components";

import categoryService from "app/common/services/categoryService";
import DashboardService from "app/components/dashboard/services/DashboardService";
import type { IDashboard } from "app/components/dashboard/services/IDashboard";
import widgetSettings from "app/components/dashboard/WidgetSettings";
import { AddToDashboardModalButton } from "app/components/React/AddToDashboard/AddToDashboardModalButton";
import { ReactButton } from "app/components/React/Button/Button";
import { allMetrics } from "app/components/widget/widget-config/metrics/metricsRegistry";
import type { IWidget, IWidgetModel } from "app/components/widget/widget-types/Widget";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { industryKeyToTitleFilter } from "app/filters/ngFilters";
import { AppStoreID } from "app/pages/app-intelligence/constants";
import { circularLoaderoptions } from "app/pages/dashboard/constants";
import { SegmentsTypeByUsage } from "app/pages/segments/analysis/constants/common";
import { AssetsService } from "app/services/AssetsService";
import { fetchAppIconsQuery, fetchWebsitesFaviconsQuery } from "app/services/sitesService/queries/sitesQueries";
import { SwTrack } from "app/services/SwTrack";
import { KeywordsGroupUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/KeywordsGroupUtilities";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface IAddToDashboardModalV2 {
    widget?: IWidget<any>;
    customModel?: IWidgetModel;
    onConfirm: (data: FixmeType) => void;
    onCancel: () => void;
}

const proModalStyles: IProModalCustomStyles = {
    content: {
        padding: 0,
        width: 400,
    },
};

type INewDashboard = Pick<IDashboard, "title"> & {
    selected?: boolean;
    id?: string;
    isAvaliable: boolean;
};

export const AddToDashboardModalV2: FC<IAddToDashboardModalV2> = (props) => {
    const { widget, customModel, onCancel } = props;
    const i18n = i18nFilter();
    const inputEl = useRef<FixmeType>(null);

    const [dashboards, setDashboards] = useState<INewDashboard[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [inEditMode, setInEditMode] = useState<boolean>(false);
    const [editableTitle, setEditableTitle] = useState<string>("");
    const [widgetTitle, setWidgetTitle] = useState<string>("");
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
    const [multipleDashboards, setMultipleDashboards] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [checkedDashboards, setCheckedDashboards] = useState<INewDashboard[]>([]);
    const [successLinkParams, setSuccessLinkParams] = useState<Array<string | { dashboardId: string }> | null>(null);
    const [isDashboardsAvaliableLoad, setIsDashboardsAvaliableLoad] = useState(false);

    const getPropsFromModel = () => {
        const segmentId = customModel?.filters?.segmentId;
        const segmentsGroupId = customModel?.filters?.segmentsGroupId;
        const customIndustryId = customModel?.customIndustryId;

        let endpointType: SegmentsTypeByUsage | null = null;

        if (segmentId) {
            endpointType = SegmentsTypeByUsage.singleSegment;
        }
        if (segmentsGroupId) {
            endpointType = SegmentsTypeByUsage.groupSegment;
        }
        if (customIndustryId) {
            endpointType = SegmentsTypeByUsage.industrySegments;
        }

        return { id: segmentId || segmentsGroupId || customIndustryId, endpointType };
    };

    const getDashboards = async () => {
        const { id, endpointType } = getPropsFromModel();
        const avaliableDashboards = new Map();

        if (id) {
            setIsDashboardsAvaliableLoad(true);

            const avaliableGroupDashboards = await DashboardService.getAvaliableDashboardsWithSegments(
                id,
                // @ts-ignore FIXME: strict migration - 'SegmentsTypeByUsage | null' not assignable to 'SegmentsTypeByUsage' — endpointType is non-null whenever id is truthy (set alongside id in getPropsFromModel)
                endpointType,
            );

            if (avaliableGroupDashboards?.length) {
                avaliableGroupDashboards.forEach((dashboard) => {
                    avaliableDashboards.set(dashboard.Id, dashboard.IsAvailable);
                });
            }

            setIsDashboardsAvaliableLoad(false);
        }

        const userDashboards = DashboardService.dashboards
            .filter((d) => !d.isSharedWithMe)
            .map((dashboard) => {
                const isAvaliable = id ? avaliableDashboards.get(dashboard.id) : true;

                return {
                    ...dashboard,
                    widgets: dashboard.widgets.map((widget: FixmeType) => ({
                        ...widget,
                    })),
                    selected: false,
                    isAvaliable,
                };
            });

        if (userDashboards.length) {
            setDashboards(userDashboards);
        } else {
            setDashboards([
                {
                    title: i18n("home.page.dashboard.first.dashboard"),
                    selected: true,
                    isAvaliable: true,
                },
            ]);
        }
    };

    useEffect(() => {
        // @ts-ignore FIXME: strict migration - 'customModel' possibly undefined / cannot index — customModel is provided whenever no widget is passed
        const metricConfig = allMetrics[customModel.metric];
        setWidgetTitle(widget ? widget.viewData.title : i18n((metricConfig?.properties?.title as string) || ""));

        getDashboards();
    }, []);

    useEffect(() => {
        setButtonDisabled(dashboards.every((dash) => !dash.selected));
    }, [dashboards]);

    function startEdit() {
        SwTrack.all.trackEvent("Pop up", "click", "Add to my Dashboard/" + widgetTitle + "/create dashboard");
        setEditableTitle(DashboardService.generateNewTitle(i18n("home.page.dashboard.new"), "title", dashboards));

        setInEditMode(true);
    }

    /**
     * Toggles CTA button if one of the dashboards enabled(checked) by user
     */
    function toggleAddButton() {
        setButtonDisabled(dashboards.every((dash) => !dash.selected));
    }

    function isDashboardTitleValid() {
        //check if there is no dashboard with same name in temp dashboard array
        const tempDashboardWithSameName = dashboards.find((dashboard) => {
            dashboard.title === editableTitle;
        });
        if (tempDashboardWithSameName) {
            return false;
        }
        return DashboardService.validateDashboardTitle({}, editableTitle);
    }

    function createDashboard() {
        if (!isDashboardTitleValid()) {
            return;
        }

        setInEditMode(false);

        setDashboards((prev) => {
            prev.unshift({
                selected: true,
                title: editableTitle,
                isAvaliable: true,
            });
            return prev;
        });

        SwTrack.all.trackEvent("Pop up", "add", "Add to my Dashboard/" + widgetTitle + "/" + editableTitle);
        setButtonDisabled(false);
    }

    function handleKeyDown(e: FixmeType) {
        if (e.keyCode === 13 || e.charCode === 13) {
            createDashboard();
        }
    }

    function handleChange(newVal: string) {
        setEditableTitle(newVal);
    }

    function handleFocus(e: FixmeType) {
        e.target.select();
    }
    useEffect(() => {
        if (inEditMode) {
            inputEl.current.select();
        }
    }, [inEditMode]);

    /**
     * Toggles dashboard in list
     * @param dashboard
     */
    function toggleCheck(dashboard: FixmeType) {
        if (!dashboard.isAvaliable) {
            return;
        }

        setDashboards((prev) => {
            const sourceType = customModel?.filters?.sourceType;
            return prev.map((dash) => {
                if (dash.id === dashboard.id) {
                    if (!dashboard.selected) {
                        SwTrack.all.trackEvent(
                            "Pop up",
                            "add",
                            "Add to my Dashboard/" + widgetTitle + "/" + dashboard.title,
                        );
                    } else {
                        SwTrack.all.trackEvent(
                            "Pop up",
                            "remove",
                            "Add to my Dashboard/" + widgetTitle + "/" + dashboard.title,
                        );
                    }
                    dash.selected = !dash.selected;
                }
                return dash;
            });
        });
        //  toggleAddButton();
    }

    /**
     * Problem: when there is no widget - no widget model
     * Solution: create custom widget model until there is no not widget left
     * @param widgetFromProp
     * @returns IWidgetModel
     */
    async function getWidgetModel(widgetFromProp: FixmeType): Promise<IWidgetModel> {
        let _model: any;
        let _customFilters: any;

        if (widgetFromProp) {
            _model = widgetFromProp.getWidgetModel();
            if (widgetFromProp._viewData.key) {
                widgetFromProp._viewData.key.forEach((item: FixmeType, i: number) => {
                    if (_model.key[i] && _model.key[i].image === undefined) {
                        _model.key[i].image = item.image;
                    }
                });
            }
            const _defaultFilters = widgetSettings.getMetricWidgetFilters(
                _model.metric,
                _model.type,
                _model.key.length > 1,
                true,
            );
            _customFilters = {
                // @ts-ignore FIXME: strict migration - 'widget' possibly undefined — widget is defined whenever widgetFromProp is truthy (widgetFromProp = widget || false)
                timeGranularity: widget.apiParams.timeGranularity ? widget.apiParams.timeGranularity : "Daily",
                ShouldGetVerifiedData: _model.ShouldGetVerifiedData,
            };

            if (_model.family === "Website") {
                // @ts-ignore FIXME: strict migration - element implicitly any indexing '{}' — getMetricWidgetFilters returns an untyped filters object
                _defaultFilters["includeSubDomains"] = widgetFromProp.apiParams.includeSubDomains;
            }
            Object.keys(_defaultFilters).forEach((filterName) => {
                if (widgetFromProp.apiParams[filterName] !== undefined) {
                    // @ts-ignore FIXME: strict migration - element implicitly any indexing '{}' — getMetricWidgetFilters returns an untyped filters object
                    _defaultFilters[filterName] = widgetFromProp.apiParams[filterName];
                }
            });
            _model["filters"] = Object.assign(
                _defaultFilters,
                _model["filters"] || {},
                _customFilters,
                // @ts-ignore FIXME: strict migration - 'widget' possibly undefined / cannot invoke — widget is defined whenever widgetFromProp is truthy
                widget.getWidgetFilters(),
            );
            //If this._widget.getWidgetFilters() is not implemented - copy the apiParams object [SIM-20263].
            // @ts-ignore FIXME: strict migration - 'widget' possibly undefined / cannot invoke — widget is defined whenever widgetFromProp is truthy
            if (!Object.keys(widget.getWidgetFilters()).length) {
                _model["filters"].filter = widgetFromProp.apiParams.filter;
            }
            if (_model.family === "Industry") {
                _model.key[0].name = industryKeyToTitleFilter()(_model.key[0].name);
                //Custom categories uses GUID in dashboards.
                if (_model.key[0].id.indexOf("*") > -1) {
                    _model.key[0].id = categoryService.getCategory(`*${_model.key[0].name}`).categoryId;
                }
                _model.key[0].category = _model.key[0].id;
            }
            if (_model.family === "Keyword") {
                //Keyword groups uses GUID in dashboards.
                if (_model.key[0].id.indexOf("*") > -1) {
                    const groupName = _model.key[0].name.split("*")[1];
                    _model.key[0].name = groupName;
                    const group = await KeywordsGroupUtilities.getGroupByName(groupName);
                    // @ts-ignore FIXME: strict migration - 'group' possibly undefined — group is expected to exist for the given keyword group name
                    _model.key[0].id = group.Id;
                }
            }
        } else {
            _model = customModel;
        }
        return _model;
    }

    function getIcon(item: any) {
        return item.image;
    }

    async function getIcons(widgetModel: IWidgetModel) {
        const query: string[] = [];
        // @ts-ignore FIXME: strict migration - 'widgetModel.key' possibly null/undefined — key is present for models processed here
        widgetModel.key.forEach((key: any) => {
            if (!getIcon(key)) {
                query.push((key.id ?? key?.name) as string);
            }
        });

        if (query.length > 0) {
            let favIcons = {};

            if (widgetModel.family === "Mobile") {
                // @ts-ignore FIXME: strict migration - 'string | null' converted to 'number' — store index expected numeric at runtime
                const store = get(widgetModel, "key[0].store", null) as number;
                // @ts-ignore FIXME: strict migration - element implicitly any indexing enum with number — store is a valid AppStoreID key
                favIcons = await fetchAppIconsQuery(query, AppStoreID[store]);
            } else {
                favIcons = await fetchWebsitesFaviconsQuery(query);
            }

            const model = { ...widgetModel };
            // @ts-ignore FIXME: strict migration - 'model.key' possibly null/undefined — key is present for models processed here
            model.key = model.key.map((key: any) => {
                // @ts-ignore FIXME: strict migration - element implicitly any indexing '{}' — favIcons is an untyped id->icon map
                if (!getIcon(key) && favIcons[key.id]) {
                    // @ts-ignore FIXME: strict migration - element implicitly any indexing '{}' — favIcons is an untyped id->icon map
                    key.image = favIcons[key.id];
                }
                return key;
            });
            return model;
        } else {
            return widgetModel;
        }
    }

    function createWidget(widgetModel: IWidgetModel) {
        const _widgetTypeWidth = widgetModel.width ?? parseInt(widgetSettings.getDefaultWidgetSize(widgetModel.type));

        return {
            pos: JSON.stringify({ sizeX: _widgetTypeWidth }), //Set default widget type width (sizeX) if width is not defined
            properties: JSON.stringify(widgetModel),
            segmentId: widgetModel?.filters?.segmentId,
            segmentsGroupId: widgetModel?.filters?.segmentsGroupId,
            customIndustryId: widgetModel?.customIndustryId,
        };
    }

    function createDashboardsQuery(dashboards: FixmeType, widget: FixmeType) {
        const _query: FixmeType[] = [];
        dashboards.forEach((dashboard: FixmeType) => {
            const _dashboard: any = {
                widgets: [widget],
            };
            if (dashboard.id) {
                _dashboard.id = dashboard.id;
            } else {
                _dashboard.title = dashboard.title;
            }
            _query.push(_dashboard);
        });
        return _query;
    }

    async function saveDashboards(dashboardQuery: FixmeType) {
        const startTime = new Date().getTime();
        const desiredTimeOut = 1000; //milliseconds
        let successLinkParams;
        const dashboardsResult = await DashboardService.bulkAddDashboards(dashboardQuery);
        // @ts-ignore FIXME: strict migration - 'dashboardsResult' possibly undefined — bulkAddDashboards resolves to the created dashboards array
        if (dashboardsResult.length === 1) {
            // @ts-ignore FIXME: strict migration - 'dashboardsResult' possibly undefined — bulkAddDashboards resolves to the created dashboards array
            successLinkParams = ["dashboard-exist", { dashboardId: dashboardsResult[0].id }];
        } else {
            successLinkParams = ["dashboard-new"];
        }

        return successLinkParams;
    }

    async function addWidget() {
        setIsSaving(true);

        const checked = dashboards.filter((dash) => dash.selected === true);

        SwTrack.all.trackEvent("Pop up", "submit-ok", "Add to my Dashboard/" + widgetTitle + "/" + checked.length);

        const _widgetProps = widget || false;
        const widgetModelOrig = await getWidgetModel(_widgetProps);
        const widgetModel = await getIcons(widgetModelOrig);
        const _widget = createWidget(widgetModel);

        setCheckedDashboards(checked);
        setMultipleDashboards(checkedDashboards.length > 1);

        const dashboardQuery = createDashboardsQuery(checked, _widget);
        const successLinkParams = await saveDashboards(dashboardQuery);

        setSuccessLinkParams(successLinkParams);
        setSuccess(true);
    }

    function cta() {
        if (!isSaving) {
            addWidget();
        } else if (isSaving && success) {
            SwTrack.all.trackEvent(
                "Internal Link",
                "submit-ok",
                "Add to my Dashboard/" + widgetTitle + "/" + checkedDashboards.length,
            );
            // eslint-disable-next-line prefer-spread
            // @ts-ignore FIXME: strict migration - apply args tuple mismatch — successLinkParams holds dynamic proNavigator.go arguments
            proNavigator.go.apply(proNavigator, successLinkParams);
        } else {
            return false;
        }
    }

    const listItems = dashboards.map((dashboard, index) => {
        const isAvaliable = dashboard?.isAvaliable;

        return (
            <li
                key={index}
                className={cn({
                    dashboardItemDisabled: !isAvaliable,
                })}
            >
                <div className="dashboardTitle">{dashboard.title}</div>
                {!isAvaliable ? (
                    <PlainTooltip
                        appendTo=".ReactModal__Content"
                        cssClass="PlainTooltip-element modalTooltipWrapper"
                        placement="bottom"
                        tooltipContent={translate("common.addtodashboard.wizard.unavailable.dashboard.tooltip")}
                    >
                        <span className="iconWrapper">
                            <SWReactIcons
                                iconName="info"
                                size="xs"
                            />
                        </span>
                    </PlainTooltip>
                ) : null}
                <div
                    className={`add-to-dashboard-modal-checkbox ${
                        !dashboard.selected
                            ? "add-to-dashboard-modal-checkbox--disabled"
                            : " add-to-dashboard-modal-checkbox--selected"
                    }`}
                    onClick={() => toggleCheck(dashboard)}
                >
                    <SWReactIcons
                        iconName="checked"
                        size="xs"
                        className="add-to-dashboard-modal-checkbox__icon"
                    />
                </div>
            </li>
        );
    });

    return (
        <ProModal
            className="add-to-dashboard-modal"
            isOpen={true}
            customStyles={proModalStyles}
            showCloseIcon={false}
        >
            <div className="sw-modal add-to-dashboard-modal-content">
                {!isSaving ? (
                    <section className={"add-to-dashboard-modal-header"}>
                        <h2>{i18n("common.addtodashboard.wizard.title")}</h2>
                        {/* when replacing to the new design change the subtitle to this: common.addtodashboard.wizard.subtitleNew */}
                        <h3>{i18n("common.addtodashboard.wizard.subtitleNew")}</h3>
                    </section>
                ) : null}
                {!isSaving ? (
                    isDashboardsAvaliableLoad ? (
                        <div className="dashboardListLoading">
                            <CircularLoader options={circularLoaderoptions} />
                        </div>
                    ) : (
                        <ul>
                            <li className="add-to-dashboard-modal__input-container">
                                <span
                                    onClick={startEdit}
                                    className={cn({
                                        displayItem: !inEditMode,
                                        hideItem: inEditMode,
                                    })}
                                >
                                    + {i18n("common.addtodashboard.wizard.createNew")}
                                </span>
                                <Textfield
                                    className={cn({
                                        "displayItem fadeInput": inEditMode,
                                        "hideItem fadeInput": !inEditMode,
                                    })}
                                    onKeyDown={handleKeyDown}
                                    onChange={handleChange}
                                    placeholder={editableTitle}
                                    defaultValue={editableTitle}
                                    autoSelectWhenFocused={true}
                                    ref={inputEl}
                                    onFocus={handleFocus}
                                />
                                {inEditMode && isDashboardTitleValid() ? (
                                    <div onClick={createDashboard}>
                                        <SWReactIcons
                                            iconName="checked"
                                            size="xs"
                                            className="add-to-dashboard-modal__add-icon"
                                        />
                                    </div>
                                ) : null}
                            </li>
                            {listItems}
                        </ul>
                    )
                ) : null}

                {/*This is PAGE 2*/}
                {isSaving ? (
                    <div className="add-to-dashboard-modal-success">
                        <h2>
                            {i18n(
                                multipleDashboards
                                    ? "common.addtodashboard.wizard.adding.multiple"
                                    : "common.addtodashboard.wizard.adding",
                            )}
                        </h2>
                        <img
                            src={AssetsService.assetUrl("/images/dashboard/Metric-Added.gif")}
                            alt={"Metric added"}
                        />
                    </div>
                ) : null}

                {!isSaving ? (
                    <div className="first-buttons">
                        {!isSaving || (isSaving && success) ? (
                            <ReactButton
                                text="common.addtodashboard.wizard.cta.cancel"
                                type="flat"
                                onClick={onCancel}
                            />
                        ) : null}
                        <AddToDashboardModalButton
                            onClick={cta}
                            isDisabled={buttonDisabled || (isSaving && !success)}
                            isSaving={isSaving}
                            isSuccess={success}
                            multipleDashboards={multipleDashboards}
                        />
                    </div>
                ) : null}

                {isSaving && success ? (
                    <div className="second-buttons">
                        <AddToDashboardModalButton
                            onClick={cta}
                            isDisabled={buttonDisabled || (isSaving && !success)}
                            isSaving={isSaving}
                            isSuccess={success}
                            multipleDashboards={multipleDashboards}
                            type="flat"
                        />
                        {!isSaving ||
                            (isSaving && success && (
                                <ReactButton
                                    text="common.addtodashboard.wizard.done"
                                    onClick={onCancel}
                                />
                            ))}
                    </div>
                ) : null}
            </div>
        </ProModal>
    );
};
